import { format } from 'date-fns';
import { GroupedEarning, WalletEarning } from '../types/WalletEarning';
import { WalletEarningsPaginated } from '../types/WalletEarningsPaginated';
import { WalletStatement } from '../types/WalletStatement';
import { WalletTransactionsPaginated } from '../types/WalletTransactionsPaginated';
import { dateFormatToBackend } from '../utils/dateUtils';
import { ApiService } from './apiService';

export class WalletService {
  private apiService = new ApiService();

  async getSummary(): Promise<WalletStatement> {
    const response = await this.apiService.get('/api/wallet/statement');
    const json = response.data as Record<string, any>;
    return WalletStatement.fromJson(json);
  }

  async getEarnings(): Promise<WalletEarning> {
    const response = await this.apiService.get('/api/wallet/earnings');
    const json = response.data as Record<string, any>;
    return WalletEarning.fromJson(json);
  }

  async getEarningsSummaryDateWise(start: Date, end: Date): Promise<any[]> {
    const response = await this.apiService.get(
      '/api/wallet/earnings_total_datewise',
      {
        queryParams: {
          start_date: format(start, 'yyyy-MM-dd'),
          end_date: format(end, 'yyyy-MM-dd'),
        },
      }
    );

    const json = response.data as Record<string, any>;
    const earningsDateWise = json.earnings_datewise as Record<string, string>;

    const earningsCategoryWise = (json.earnings_datewise_grouped as any[]).map(
      (e) => GroupedEarning.fromJson(e as Record<string, any>)
    );

    return [earningsDateWise, earningsCategoryWise];
  }

  async getTransactionsPaginated({
    cursor,
    transactionKeys,
    transactionTypes,
    sortBy,
    startDate,
    endDate,
    dateRange,
  }: {
    cursor?: string | null;
    transactionKeys?: string[];
    transactionTypes?: string[];
    sortBy?: string;
    startDate?: Date;
    endDate?: Date;
    dateRange?: string;
  }): Promise<WalletTransactionsPaginated> {
    const params: Record<string, any> = {};
    if (cursor) params.cursor = cursor;
    if (sortBy) params.sort = sortBy;
    if (dateRange) params.date_range = dateRange;
    if (startDate) params.start_date = dateFormatToBackend(startDate);
    if (endDate) params.end_date = dateFormatToBackend(endDate);
    if (transactionKeys?.length) {
      transactionKeys.forEach((key, index) => {
        params[`transaction_key[${index}]`] = key;
      });
    }
    if (transactionTypes?.length === 1) params.cash_flow = transactionTypes[0];

    const response = await this.apiService.get('/api/wallet/transactions', {
      queryParams: params,
    });
    const json = response.data as Record<string, any>;
    return WalletTransactionsPaginated.fromJson(json);
  }

  async getEarningsPaginated({
    cursor,
    bonusTypes,
    sortBy,
    startDate,
    endDate,
    dateRange,
  }: {
    cursor?: string | null;
    bonusTypes?: string[];
    sortBy?: string;
    startDate?: Date;
    endDate?: Date;
    dateRange?: string;
  }): Promise<WalletEarningsPaginated> {
    const params: Record<string, any> = {};
    if (cursor) params.cursor = cursor;
    if (sortBy) params.sort = sortBy;
    if (dateRange) params.date_range = dateRange;
    if (startDate) params.start_date = dateFormatToBackend(startDate);
    if (endDate) params.end_date = dateFormatToBackend(endDate);
    if (bonusTypes?.length) {
      bonusTypes.forEach((key, index) => {
        params[`bonus_type[${index}]`] = key;
      });
    }

    const response = await this.apiService.get('/api/wallet/recent_earnings', {
      queryParams: params,
    });
    const json = response.data as Record<string, any>;
    return WalletEarningsPaginated.fromJson(json);
  }
}
